import React from "react";
import { act, render } from "@testing-library/react";
import {
  buildDataSourceSelectionKey,
  buildRuntimeFilterScopeKey,
  CanvasRuntimeProvider,
  type CanvasInteractionBus,
  useCanvasInteractionBus,
} from "./CanvasRuntimeContext";

function renderBusHarness() {
  let currentBus: CanvasInteractionBus | null = null;

  function Probe() {
    currentBus = useCanvasInteractionBus();
    return null;
  }

  render(
    <CanvasRuntimeProvider
      value={{
        workspaceId: "workspace-1",
        canvasId: "series-canvas",
        rowId: null,
        rowData: null,
      }}
    >
      <Probe />
    </CanvasRuntimeProvider>,
  );

  const getBus = () => {
    if (!currentBus) throw new Error("Canvas interaction bus not mounted");
    return currentBus;
  };

  return { getBus };
}

describe("CanvasRuntimeContext interaction bus", () => {
  it("keeps filter write callbacks stable when the filter state changes", () => {
    const { getBus } = renderBusHarness();
    const upsert = getBus().upsertRuntimeFilter;
    const remove = getBus().removeRuntimeFilter;
    act(() =>
      upsert({
        id: "embedded",
        sourceEventId: "event",
        sourceElementId: "host",
        targetCanvasId: "child",
        targetDataSourceAlias: "main",
        dependsOnSelectionKey: "parent",
        targetScopeKey: "child-scope",
        provenancePath: [],
        filters: [{ field: "parent_id", operator: "eq", value: "row" }],
      }),
    );
    expect(getBus().upsertRuntimeFilter).toBe(upsert);
    expect(getBus().removeRuntimeFilter).toBe(remove);
    act(() => remove("embedded"));
    expect(getBus().upsertRuntimeFilter).toBe(upsert);
    expect(getBus().removeRuntimeFilter).toBe(remove);
  });
  it("stores row selections under equivalent data-source aliases", () => {
    const { getBus } = renderBusHarness();

    act(() => {
      getBus().dispatchInteraction({
        type: "row_selected",
        elementId: "titles-grid",
        canvasId: "titles-canvas",
        dataSourceAlias: "titles",
        dataSourceAliases: ["main", "titles", "titles-view-source"],
        viewId: "titles-view",
        rowId: "title-1",
        rowData: { id: "title-1", title: "Title 1" },
      });
    });

    expect(
      getBus().getSelectedRow({
        canvasId: "titles-canvas",
        dataSourceAlias: "main",
      })?.rowId,
    ).toBe("title-1");
    expect(
      getBus().getSelectedRow({
        canvasId: "titles-canvas",
        dataSourceAlias: "titles-view-source",
      })?.rowId,
    ).toBe("title-1");

    act(() => {
      getBus().dispatchInteraction({
        type: "selection_cleared",
        elementId: "titles-grid",
        canvasId: "titles-canvas",
        dataSourceAlias: "main",
      });
    });

    expect(
      getBus().getSelectedRow({
        canvasId: "titles-canvas",
        dataSourceAlias: "titles",
      }),
    ).toBeNull();
    expect(
      getBus().getSelectedRow({
        canvasId: "titles-canvas",
        dataSourceAlias: "titles-view-source",
      }),
    ).toBeNull();
  });

  it("falls back to the only same-canvas selection when an embedding asks for an alias the grid did not publish", () => {
    const { getBus } = renderBusHarness();

    act(() => {
      getBus().dispatchInteraction({
        type: "row_selected",
        elementId: "titles-grid",
        canvasId: "titles-canvas",
        dataSourceAlias: "main",
        viewId: "titles-view",
        rowId: "title-1",
        rowData: { _rowId: "title-1", title: "Title 1" },
      });
    });

    expect(
      getBus().getSelectedRow({
        canvasId: "titles-canvas",
        dataSourceAlias: "titles",
      })?.rowId,
    ).toBe("title-1");

    act(() => {
      getBus().dispatchInteraction({
        type: "row_selected",
        elementId: "authors-grid",
        canvasId: "titles-canvas",
        dataSourceAlias: "authors",
        viewId: "authors-view",
        rowId: "author-1",
        rowData: { _rowId: "author-1", name: "Author 1" },
      });
    });

    expect(
      getBus().getSelectedRow({
        canvasId: "titles-canvas",
        dataSourceAlias: "titles",
      }),
    ).toBeNull();
  });

  it("clears descendant selections and filters when an ancestor selection changes", () => {
    const { getBus } = renderBusHarness();
    const seriesSelectionKey = buildDataSourceSelectionKey(
      "series-canvas",
      "series",
    );
    const titlesSelectionKey = buildDataSourceSelectionKey(
      "titles-canvas",
      "titles",
    );
    const titlesFilterScope = buildRuntimeFilterScopeKey(
      "titles-canvas",
      "titles",
    );
    const formatsFilterScope = buildRuntimeFilterScopeKey(
      "formats-canvas",
      "formats",
    );

    act(() => {
      getBus().dispatchInteraction({
        type: "row_selected",
        elementId: "series-grid",
        canvasId: "series-canvas",
        dataSourceAlias: "series",
        viewId: "series-view",
        rowId: "series-a",
        rowData: { id: "series-a", name: "Series A" },
      });
    });

    act(() => {
      getBus().upsertRuntimeFilter({
        id: "series-to-titles",
        sourceEventId: "series-selected",
        sourceElementId: "series-grid",
        sourceDataSourceAlias: "series",
        targetCanvasId: "titles-canvas",
        targetDataSourceAlias: "titles",
        dependsOnSelectionKey: seriesSelectionKey,
        targetScopeKey: titlesFilterScope,
        filters: [{ field: "series_id", operator: "eq", value: "series-a" }],
        filterLogic: "AND",
        provenancePath: [seriesSelectionKey],
      });
    });

    act(() => {
      getBus().dispatchInteraction({
        type: "row_selected",
        elementId: "titles-grid",
        canvasId: "titles-canvas",
        dataSourceAlias: "titles",
        viewId: "titles-view",
        rowId: "title-1",
        rowData: { id: "title-1", series_id: "series-a", title: "Title 1" },
        provenancePath: [seriesSelectionKey],
      });
    });

    act(() => {
      getBus().upsertRuntimeFilter({
        id: "titles-to-formats",
        sourceEventId: "title-selected",
        sourceElementId: "titles-grid",
        sourceDataSourceAlias: "titles",
        targetCanvasId: "formats-canvas",
        targetDataSourceAlias: "formats",
        dependsOnSelectionKey: titlesSelectionKey,
        targetScopeKey: formatsFilterScope,
        filters: [{ field: "title_id", operator: "eq", value: "title-1" }],
        filterLogic: "AND",
        provenancePath: [seriesSelectionKey, titlesSelectionKey],
      });
    });

    expect(
      getBus().getSelectedRow({
        canvasId: "titles-canvas",
        dataSourceAlias: "titles",
      }),
    ).not.toBeNull();
    expect(
      getBus().getRuntimeFilters({
        canvasId: "formats-canvas",
        dataSourceAlias: "formats",
      }),
    ).toHaveLength(1);

    act(() => {
      getBus().dispatchInteraction({
        type: "row_selected",
        elementId: "series-grid",
        canvasId: "series-canvas",
        dataSourceAlias: "series",
        viewId: "series-view",
        rowId: "series-b",
        rowData: { id: "series-b", name: "Series B" },
      });
    });

    expect(
      getBus().getSelectedRow({
        canvasId: "titles-canvas",
        dataSourceAlias: "titles",
      }),
    ).toBeNull();
    expect(
      getBus().getRuntimeFilters({
        canvasId: "formats-canvas",
        dataSourceAlias: "formats",
      }),
    ).toHaveLength(0);
    expect(
      getBus().getRuntimeFilters({
        canvasId: "titles-canvas",
        dataSourceAlias: "titles",
      }),
    ).toHaveLength(0);
  });

  it("drops runtime filters whose provenance would create a selection-filter cycle", () => {
    const { getBus } = renderBusHarness();
    const targetScopeKey = buildRuntimeFilterScopeKey("canvas-a", "source-a");
    const warnSpy = jest.spyOn(console, "warn").mockImplementation(() => {});

    try {
      act(() => {
        getBus().upsertRuntimeFilter({
          id: "cyclic-filter",
          sourceEventId: "event-1",
          sourceElementId: "grid-b",
          sourceDataSourceAlias: "source-b",
          targetCanvasId: "canvas-a",
          targetDataSourceAlias: "source-a",
          dependsOnSelectionKey: buildDataSourceSelectionKey(
            "canvas-b",
            "source-b",
          ),
          targetScopeKey,
          filters: [{ field: "source_b_id", operator: "eq", value: "b-1" }],
          filterLogic: "AND",
          provenancePath: [targetScopeKey],
        });
      });

      expect(warnSpy).toHaveBeenCalledWith(
        "Dropped cyclic canvas runtime filter",
        expect.objectContaining({ id: "cyclic-filter" }),
      );
      expect(
        getBus().getRuntimeFilters({
          canvasId: "canvas-a",
          dataSourceAlias: "source-a",
        }),
      ).toHaveLength(0);
    } finally {
      warnSpy.mockRestore();
    }
  });

  it("clears descendant runtime filters when a source selection is cleared", () => {
    const { getBus } = renderBusHarness();
    const seriesSelectionKey = buildDataSourceSelectionKey(
      "series-canvas",
      "series",
    );
    const titlesFilterScope = buildRuntimeFilterScopeKey(
      "titles-canvas",
      "titles",
    );

    act(() => {
      getBus().dispatchInteraction({
        type: "row_selected",
        elementId: "series-grid",
        canvasId: "series-canvas",
        dataSourceAlias: "series",
        viewId: "series-view",
        rowId: "series-a",
        rowData: { id: "series-a", name: "Series A" },
      });
      getBus().upsertRuntimeFilter({
        id: "series-to-titles",
        sourceEventId: "series-selected",
        sourceElementId: "series-grid",
        sourceDataSourceAlias: "series",
        targetCanvasId: "titles-canvas",
        targetDataSourceAlias: "titles",
        dependsOnSelectionKey: seriesSelectionKey,
        targetScopeKey: titlesFilterScope,
        filters: [{ field: "series_id", operator: "eq", value: "series-a" }],
        filterLogic: "AND",
        provenancePath: [seriesSelectionKey],
      });
    });

    expect(
      getBus().getRuntimeFilters({
        canvasId: "titles-canvas",
        dataSourceAlias: "titles",
      }),
    ).toHaveLength(1);

    act(() => {
      getBus().dispatchInteraction({
        type: "selection_cleared",
        elementId: "series-grid",
        canvasId: "series-canvas",
        dataSourceAlias: "series",
      });
    });

    expect(
      getBus().getSelectedRow({
        canvasId: "series-canvas",
        dataSourceAlias: "series",
      }),
    ).toBeNull();
    expect(
      getBus().getRuntimeFilters({
        canvasId: "titles-canvas",
        dataSourceAlias: "titles",
      }),
    ).toHaveLength(0);
  });
});
